import { FC } from 'react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import {
    CaseloadReading,
    CoverageKey,
    IAgencyAlert,
    IAgencyDigest,
    IClientWellbeing,
    IFamilyParticipation,
    IHistoryProgress,
    IOutcomeMetric,
    describeCoverage,
    describeOutcomesWait
} from '@innerhive-internal/innerhive-common';
import { IAvatarIdentity } from '../../avatar/IAvatarIdentity';
import { CaseloadSummaryReport } from './CaseloadSummaryReport';
import { NeedsAttentionCard } from './NeedsAttentionCard';
import { FamilyParticipationSection } from './FamilyParticipationSection';
import { CoverageMeters } from './CoverageMeters';
import { InsightsCard } from './InsightsCard';
import { InsightsInfoTip } from './InsightsInfoTip';
import { OutcomesReport } from './OutcomesReport';
import { OutcomeSummaries } from './OutcomeSummaries';
import { BeaSays } from './BeaSays';
import { InsightsNarration } from './InsightsNarration';
import { IInsightsClientList } from './InsightsClientListModal';
// Hidden reports, kept with the blocks below that draw them:
// import { RECENT_WEEKS, WELLBEING_EXPLAINER, describeAttention, describeBaselines, describeHistoryRule, loadDirection } from '@innerhive-internal/innerhive-common';
// import { NeedsHistory } from './NeedsHistory';
// import { BaselineDeviationStrip, DEVIATION_EXPLAINER } from './BaselineDeviationStrip';
// import { WellbeingRow } from './WellbeingRow';
// import { CaseloadPulseChart } from './CaseloadPulseChart';
// import { AttentionTrend } from './AttentionTrend';
// import { InsightsAskBea } from './InsightsAskBea';

export interface IInsightsReportsProps {
    digest: IAgencyDigest;
    wellbeing: IClientWellbeing[];
    history: IHistoryProgress;
    participation: IFamilyParticipation;
    outcomes: IOutcomeMetric[];
    /** Every finding in scope, which the Worth Watching section groups by client. */
    alerts: IAgencyAlert[];
    /** Whether the reader narrowed the findings to one kind, which leaves out a client with none of that kind. */
    kindNarrowed: boolean;
    /** The reading the reader narrowed the page to, when they did. */
    readingNarrowed?: CaseloadReading;
    /** When the page is narrowed, the clients still in scope. Everyone else stays but recedes. */
    matchingIds?: string[];
    narrowed: boolean;
    openMeter?: CoverageKey;
    /** How each client is drawn, with the audited link to a hosted photo. */
    clientAvatar: (careMapId: string) => IAvatarIdentity | undefined;
    /** Each client's reading, which every list draws beside their name as the map draws their cell. */
    clientReading: (careMapId: string) => CaseloadReading | undefined;
    onToggleMeter: (key: CoverageKey) => void;
    /** Narrows the page to a reading, or widens it back when it already is. */
    onToggleReading: (reading: CaseloadReading) => void;
    onClearFilter: () => void;
    onOpenClient: (careMapId: string) => void;
    /** Narrows the page to the clients drawn red. */
    onShowIncidents: () => void;
    onOpenOutcome: (metric: IOutcomeMetric) => void;
    /** Opens the clients behind a participation figure. */
    onOpenList: (list: IInsightsClientList) => void;
}

/**
 * The reports of the Insights page, in reading order: who to deal with first and who else is worth
 * watching, where to look today, what the caseload can evidence about itself and who is contributing,
 * and what is on file.
 *
 * Every report is drawn from the one digest it is handed, so no two can disagree about a client.
 * The caseload summary and the worklist open the same detail view, because the question a reader
 * has after spotting someone is always the same one: what actually happened.
 *
 * The one rule the whole page is built around: nothing here claims Innerhive kept a client home or
 * out of hospital. Nobody has measured the counterfactual. Every figure is a statement about what is
 * on the record, and each explains exactly what it counted.
 *
 * A caseload too young to chart keeps every report that needs no comparison; the ones that do wait
 * on its history and say what for.
 */
export const InsightsReports: FC<IInsightsReportsProps> = ({
    digest, history, participation, outcomes, alerts, kindNarrowed, readingNarrowed, matchingIds, narrowed, openMeter, clientAvatar, clientReading,
    onToggleMeter, onToggleReading, onClearFilter, onOpenClient, onShowIncidents, onOpenOutcome, onOpenList
}) => {
    const narration = useInstance(InsightsNarration);

    const { shown: shownOutcomes, waiting: waitingOutcomes } = narration.splitOutcomes({ outcomes, history });

    return (
        <>
            <NeedsAttentionCard
                digest={digest}
                alerts={alerts}
                kindNarrowed={kindNarrowed}
                readingNarrowed={readingNarrowed}
                matchingIds={matchingIds}
                narrowed={narrowed}
                clientAvatar={clientAvatar}
                clientReading={clientReading}
                onOpenClient={onOpenClient}
                onShowIncidents={onShowIncidents}
            />

            <CaseloadSummaryReport
                digest={digest}
                history={history}
                matchingIds={matchingIds}
                readingNarrowed={readingNarrowed}
                narrowed={narrowed}
                clientAvatar={clientAvatar}
                onOpenClient={onOpenClient}
                onToggleReading={onToggleReading}
                onClearFilter={onClearFilter}
            />

            <InsightsCard
                heading="Outcomes Report"
                action={(
                    <InsightsInfoTip label="How these figures are counted">
                        <OutcomeSummaries definitions={outcomes.map(metric => metric.definition)} />
                    </InsightsInfoTip>
                )}
            >
                <OutcomesReport
                    metrics={shownOutcomes}
                    pending={waitingOutcomes.length > 0
                        ? describeOutcomesWait({ waiting: waitingOutcomes.map(metric => metric.title), history })
                        : undefined}
                    onOpen={onOpenOutcome}
                />

                <FamilyParticipationSection
                    participation={participation}
                    unreadableCount={digest.unreadableCount}
                    onOpenList={onOpenList}
                />
            </InsightsCard>

            {/* Last because it is the only report that is not about this week. What is on file is a
                standing worklist rather than something that changed, so it sits under the things a
                reader opens the page for. */}
            <InsightsCard
                heading="Document Coverage"
                action={<InsightsInfoTip label="What coverage counts" summary="On file documentation for each client. Expand a section to see which clients have gaps in coverage." />}
            >
                <CoverageMeters digest={digest} openMeter={openMeter} clientAvatar={clientAvatar} onToggleMeter={onToggleMeter} />

                <BeaSays panel="coverage">{describeCoverage({ digest })}</BeaSays>
            </InsightsCard>

            {/* Hidden from the page. Each block keeps its report whole, with its data-id and Bea's
                line. Restoring one also needs what the page used to pass in: Need Attention the
                caseload wellbeing (`wellbeing` from the figures), Baseline Trends the narrowed
                baselines (filterBaselines(digest, filter)), and Caseload Activity the selected
                finding kind, the week opener (observationsInWeek into the client list modal), the
                kind toggle and this constant:
                const ACTIVITY_EXPLAINER = 'Each observation sits in the week it describes. Open a bar to read that week rather than to filter the page.';

            <InsightsCard
                heading="Need Attention"
                action={(
                    <InsightsInfoTip label="About who needs attention" summary="Clients whose recent weeks look the least like their typical baseline.">
                        {history.hasEnough ? WELLBEING_EXPLAINER : describeHistoryRule(history)}
                    </InsightsInfoTip>
                )}
            >
                <NeedsHistory history={history} panel="attention">
                    <WellbeingRow wellbeing={wellbeing} onOpenClient={onOpenClient} />

                    <BeaSays panel="attention">{describeAttention(digest)}</BeaSays>
                </NeedsHistory>
            </InsightsCard>

            <InsightsCard
                heading="Baseline Trends"
                action={(
                    <InsightsInfoTip
                        label="What the figure means"
                        summary={`Observational trends across the last ${RECENT_WEEKS} weeks compared to the same client's earlier baseline.`}
                    >
                        {history.hasEnough ? DEVIATION_EXPLAINER : describeHistoryRule(history)}
                    </InsightsInfoTip>
                )}
            >
                <NeedsHistory history={history} panel="baselines">
                    <BaselineDeviationStrip
                        unreadableCount={digest.unreadableCount}
                        baselines={baselines}
                        narrowed={narrowed}
                        onSelectClient={onOpenClient}
                    />

                    <BeaSays panel="baselines">{describeBaselines({ baselines, narrowed, unreadableCount: digest.unreadableCount })}</BeaSays>
                </NeedsHistory>
            </InsightsCard>

            <InsightsAskBea />

            <InsightsCard
                heading="Caseload Activity"
                action={(
                    <InsightsInfoTip label="About caseload activity" summary="Observations recorded each week, categorized by type.">
                        {history.hasEnough ? ACTIVITY_EXPLAINER : describeHistoryRule(history)}
                    </InsightsInfoTip>
                )}
            >
                <NeedsHistory history={history} panel="activity">
                    <CaseloadPulseChart
                        weekly={digest.weekly}
                        selectedKind={selectedKind}
                        onOpenWeek={onOpenWeek}
                        onSelectKind={onSelectKind}
                    />

                    <BeaSays panel="activity">
                        I sorted every observation into the week it describes. The coral band is what I would
                        look at first: those are the weeks where somebody moved against their own baseline.
                    </BeaSays>
                </NeedsHistory>
            </InsightsCard>

            <InsightsCard
                heading="Week Over Week"
                action={(
                    <InsightsInfoTip label="About week over week" summary="Earlier weeks against recent ones, across the whole caseload.">
                        {!history.hasEnough && describeHistoryRule(history)}
                    </InsightsInfoTip>
                )}
            >
                <NeedsHistory history={history} panel="week-over-week">
                    <AttentionTrend digest={digest} />

                    <BeaSays panel="week-over-week">
                        {loadDirection(digest) === 'rising'
                            ? 'The caseload is generating more to look at than it was earlier in the window. That is usually more families reporting rather than more going wrong, but it is worth knowing which.'
                            : loadDirection(digest) === 'easing'
                                ? 'Quieter than earlier in the window. Worth checking that families are still reporting before reading it as everyone settling.'
                                : 'Holding about level with earlier in the window.'}
                    </BeaSays>
                </NeedsHistory>
            </InsightsCard>
            */}
        </>
    );
};
